<script setup>
import { ref, computed, defineEmits, watch } from 'vue';
import { formatMonthLabel } from '@/js/core/utils/month-range.js';
import {
  calendarEventPlacements,
  calendarEventRangeInfo,
  calendarEventScheduleLabel,
  calendarEventWeekPlacementsFromProjectedEvents,
} from '@/js/core/utils/calendar-event-projections.js';
import {
  calendarEventDisplayTime,
  calendarEventMultiDayTime,
} from '@/js/core/utils/calendar-event-time.js';
import { EVENT_EXPIRY_PRECISION, isEventExpired } from '@/js/core/utils/event-expiry.js';
import CalendarPlusIcon from '@/js/core/components/icons/CalendarPlusIcon.vue';
import CalendarEventTime from '@/js/core/components/parts/calendar/CalendarEventTime.vue';
import {
  getCalendarViewingMonth,
  getCurrentCalendarDateKey,
  getEventFilterGroup,
  isCalendarCurrentMonth,
} from '@/js/core/utils/calendar-events.js';
import {
  calendarEventNavigationModeProp,
  useCalendarEventNavigation,
} from '@/js/core/composables/useCalendarEventNavigation.js';
import { DEFAULT_TIMEZONE } from '@/js/core/utils/timezone.js';
import { wrapBreakableTitle, plainTextFromHtml } from '@/js/core/utils/formatters.js';

const props = defineProps({
  events: { type: Array, default: () => [] },
  activeRange: { type: Object, default: () => ({}) },
  projectedEvents: { type: Array, default: null },
  showPastEvents: { type: Boolean, default: false },
  canTogglePastEvents: { type: Boolean, default: true },
  hideExpiredEvents: { type: Boolean, default: false },
  dimPastEvents: { type: Boolean, default: true },
  limitDisplayedEvents: { type: Boolean, default: true },
  alwaysShowYear: { type: Boolean, default: false },
  showMonthName: { type: Boolean, default: false },
  timezone: { type: String, default: DEFAULT_TIMEZONE },
  eventFilterGroups: { type: Object, default: null },
  eventFilterFallbackGroup: { type: String, default: 'Other' },
  eventNavigationMode: calendarEventNavigationModeProp,
});

const expandedDays = ref({});
const emit = defineEmits(['toggle-past-events']);
const { eventNavigation, handleEventClick, handleEventKeydown } = useCalendarEventNavigation(props);

const eventHasTime = event => Boolean(calendarEventDisplayTime(event, props.timezone));

const isPastEvent = event =>
  isEventExpired(event, {
    timezone: props.timezone,
    precision: props.hideExpiredEvents ? EVENT_EXPIRY_PRECISION.time : EVENT_EXPIRY_PRECISION.day,
  });

const scheduleLabel = event => calendarEventScheduleLabel(event, props.timezone);

const multiDayTime = event => {
  const timed = calendarEventMultiDayTime(event, props.timezone, { showTimezone: true });
  if (timed) {
    return timed;
  }

  // No-time multi-day events show their date range (matching the Categories view).
  const rangeInfo = calendarEventRangeInfo(event, props.timezone);
  return rangeInfo.isRange && !eventHasTime(event) ? rangeInfo.inlineDateLabel : '';
};

const currentDateKey = () => getCurrentCalendarDateKey(props.timezone);

const viewingMonth = computed(() => getCalendarViewingMonth(props.activeRange, props.timezone));

const isCurrentMonth = computed(() => isCalendarCurrentMonth(props.activeRange, props.timezone));

const formattedSelectedMonth = computed(() =>
  formatMonthLabel(viewingMonth.value, props.alwaysShowYear)
);

const projectedEventsForCalendar = computed(
  () => props.projectedEvents || calendarEventPlacements(props.events, { timezone: props.timezone })
);

const togglePastEvents = () => emit('toggle-past-events');

watch(
  () => props.activeRange,
  () => {
    expandedDays.value = {};
  },
  { deep: true }
);

const toggleDayExpansion = dayKey => {
  expandedDays.value[dayKey] = !expandedDays.value[dayKey];
};

const itemKey = item => (item.kind === 'event' ? `event-${item.event.id}` : `more-${item.dayKey}`);

const truncateTitleWords = (title, maxWords) => {
  const words = String(title || '')
    .trim()
    .split(/\s+/);

  return words.length > maxWords ? `${words.slice(0, maxWords).join(' ')}...` : title;
};

const weeks = computed(() => {
  const todayStr = currentDateKey();

  const [year, month] = viewingMonth.value.split('-').map(Number);

  const startOfMonth = new Date(year, month - 1, 1);
  const startOfFirstWeek = new Date(startOfMonth);
  startOfFirstWeek.setDate(startOfMonth.getDate() - startOfMonth.getDay());

  const tempWeeks = [];
  let runner = new Date(startOfFirstWeek);

  for (let i = 0; i < 6; i++) {
    const weekDays = [];
    for (let d = 0; d < 7; d++) {
      const dayDate = new Date(runner);
      const dayString = [
        dayDate.getFullYear(),
        String(dayDate.getMonth() + 1).padStart(2, '0'),
        String(dayDate.getDate()).padStart(2, '0'),
      ].join('-');

      weekDays.push({
        dateString: dayString,
        dayName: dayDate.toLocaleDateString('en-US', { weekday: 'short' }),
        dayNumber: dayDate.getDate(),
        isToday: dayString === todayStr,
        isPast: dayString < todayStr,
        isCurrentMonth: dayDate.getMonth() === month - 1,
      });
      runner.setDate(runner.getDate() + 1);
    }

    const isWeekPast = weekDays.every(d => d.isPast);
    const hasDaysInMonth = weekDays.some(d => d.isCurrentMonth);

    if (hasDaysInMonth) {
      const weekKey = weekDays[0]?.dateString || `week-${i}`;
      const placementSet = calendarEventWeekPlacementsFromProjectedEvents(
        projectedEventsForCalendar.value,
        weekDays,
        {
          limitDisplayedEvents: props.limitDisplayedEvents,
          expandedDays: expandedDays.value,
        }
      );

      tempWeeks.push({
        days: weekDays,
        isPast: isWeekPast,
        weekKey,
        ...placementSet,
      });
    }
  }
  return tempWeeks;
});

const displayedWeeks = computed(() => {
  if (props.showPastEvents) return weeks.value;

  if (!isCurrentMonth.value) return weeks.value;

  return weeks.value.filter(week => !week.isPast);
});
</script>

<template>
  <div class="calendar-view-grid">
    <div v-if="showMonthName" class="calendar-month-name">
      <h2>{{ formattedSelectedMonth }}</h2>
    </div>

    <div v-if="canTogglePastEvents && isCurrentMonth" class="view-past-event-toggle">
      <button type="button" class="view-past-event-toggle__link" @click="togglePastEvents">
        {{ props.showPastEvents ? 'hide past events' : 'show past events' }}
      </button>
    </div>

    <div v-for="(week, wIdx) in displayedWeeks" :key="wIdx" class="calendar-week">
      <div class="calendar-week-container">
        <div
          v-for="(day, dIdx) in week.days"
          :key="`header-${day.dateString}`"
          class="day-header"
          :class="{ 'is-today': day.isToday, 'is-past': dimPastEvents && day.isPast }"
          :style="`grid-column: ${dIdx + 1}; grid-row: 1;`"
        >
          <div class="day-name">{{ day.dayName }}</div>
          <div class="day-number">
            {{ day.dayNumber }}
            <span v-if="day.isToday" class="today-label">Today</span>
          </div>
        </div>

        <template v-for="item in week.renderItems" :key="itemKey(item)">
          <div
            v-if="item.kind === 'event'"
            v-show="!item.hidden"
            class="calendar-event-card"
            :class="{
              past: dimPastEvents && isPastEvent(item.event),
              'multi-day': item.event.is_multi_day,
              'starts-before': item.event.starts_before_week,
              'ends-after': item.event.ends_after_week,
            }"
            :data-filter-group="
              getEventFilterGroup(
                item.event.category,
                props.eventFilterGroups || undefined,
                props.eventFilterFallbackGroup
              )
            "
            :data-linkable="eventNavigation(item.event).isInteractive ? 'true' : undefined"
            :style="`grid-column: ${item.event.grid_start} / span ${item.event.grid_span};`"
          >
            <div class="event-content">
              <component
                :is="eventNavigation(item.event).tag"
                class="event-link"
                :href="eventNavigation(item.event).href"
                :style="{
                  cursor: eventNavigation(item.event).isInteractive ? 'pointer' : 'default',
                }"
                @click="handleEventClick($event, item.event)"
                @keydown="e => handleEventKeydown(e, item.event)"
              >
                <span class="event-type">{{ item.event.category }}</span>
                <div v-if="(item.event.audience_labels || []).length" class="event-audiences">
                  <div
                    v-for="audience in item.event.audience_labels"
                    :key="audience"
                    class="event-audience"
                  >
                    {{ audience }}
                  </div>
                </div>
                <h2
                  class="event-title"
                  v-html="
                    wrapBreakableTitle(
                      item.crowded ? truncateTitleWords(item.event.title, 8) : item.event.title
                    )
                  "
                ></h2>
                <span v-if="scheduleLabel(item.event)" class="visually-hidden">
                  {{ scheduleLabel(item.event) }}
                </span>
              </component>
              <div class="event-footer">
                <span
                  v-if="multiDayTime(item.event)"
                  class="time"
                  v-html="multiDayTime(item.event)"
                ></span>
                <CalendarEventTime
                  v-else
                  :event="item.event"
                  :timezone="props.timezone"
                  class="time"
                />
                <a
                  v-if="item.event.ical_download_url"
                  :href="item.event.ical_download_url"
                  class="calendar-add-btn"
                  download
                  :aria-label="`Download calendar file for ${plainTextFromHtml(item.event.title)}`"
                >
                  <CalendarPlusIcon class="calendar-icon" />
                </a>
              </div>
            </div>
          </div>

          <div
            v-else
            class="more-events-wrapper"
            :style="`grid-column: ${item.grid_start} / span ${item.grid_span};`"
          >
            <button
              class="button-more"
              :aria-expanded="item.expanded ? 'true' : 'false'"
              @click="toggleDayExpansion(item.dayKey)"
            >
              <span v-if="!item.expanded">show {{ item.remainingCount }} more ...</span>
              <span v-else>hide {{ item.remainingCount }} more ...</span>
            </button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
